import React, { useMemo } from 'react';
import { Timeline, Typography } from '@saviynt/design-system';

import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import {
  type HistoryTimelineMappedData,
  type RootState,
} from '../../../shared/types';
import { selectReviewActivitiesByLogId } from '../data-store/auditLogSlice';
import { useDateFormat } from '../hooks';
import { getDisplayFormatForGlobalDateFormat } from '../utils/utils';

interface ReviewActivityLike {
  status: 'OPEN' | 'FLAGGED' | 'RESOLVED' | 'COMMENTED' | string;
  user: string;
  comment: string;
  updatedAt?: string | number | Date;
  time?: string | number | Date;
}

interface AuditLogViewerSidePanelHistoryProps {
  sodKey: string;
  activities?: ReviewActivityLike[];
}

/**
 * Renders a timeline of review activities for an audit log item.
 */
function AuditLogViewerSidePanelHistory({
  sodKey,
  activities,
}: AuditLogViewerSidePanelHistoryProps): React.ReactElement | null {
  const l = useInvertedLocalize();
  const globalDateFormat = useDateFormat();
  const dateFormat = getDisplayFormatForGlobalDateFormat(globalDateFormat);
  const reduxActivities = useTypedSelector((state: RootState) =>
    // Cast selector to accept RootState without narrowing the slice
    (
      selectReviewActivitiesByLogId as unknown as (
        s: RootState,
        k: string
      ) => ReviewActivityLike[]
    )(state, sodKey)
  );

  const source = useMemo<ReviewActivityLike[]>(() => {
    if (Array.isArray(activities)) {
      return activities;
    }

    if (Array.isArray(reduxActivities)) {
      return reduxActivities;
    }

    return [];
  }, [activities, reduxActivities]);
  const timelineBlocksData = useMemo(() => {
    const blocks: HistoryTimelineMappedData[] = source.map((ra) => {
      let resolvedColorTheme: HistoryTimelineMappedData['colorTheme'];

      if (ra.status === 'RESOLVED') {
        resolvedColorTheme =
          'positive' as unknown as HistoryTimelineMappedData['colorTheme'];
      } else if (ra.status === 'FLAGGED') {
        resolvedColorTheme =
          'warning' as unknown as HistoryTimelineMappedData['colorTheme'];
      } else {
        resolvedColorTheme =
          'secondary' as unknown as HistoryTimelineMappedData['colorTheme'];
      }

      const dateInput = ra.updatedAt ?? ra.time ?? Date.now();

      let dateValue: Date;

      if (dateInput instanceof Date) {
        dateValue = dateInput;
      } else {
        dateValue = new Date(dateInput);
      }

      let userLabel = l('User');

      if (ra.status === 'FLAGGED') {
        userLabel = l('Flagged By');
      } else if (ra.status === 'RESOLVED') {
        userLabel = l('Resolved By');
      } else if (ra.status === 'COMMENTED') {
        userLabel = l('Commented By');
      }

      let activityTitle = ra.status;

      if (ra.status === 'FLAGGED') {
        activityTitle = l('Flagged');
      } else if (ra.status === 'RESOLVED') {
        activityTitle = l('Flag Resolved');
      } else if (ra.status === 'COMMENTED') {
        activityTitle = l('Commented');
      }

      let iconKind = 'Activity';
      let medallionKind: HistoryTimelineMappedData['medallionKind'] = 'filled';

      if (ra.status === 'FLAGGED') {
        iconKind = 'FlagFilled';
        medallionKind = 'outlined';
      } else if (ra.status === 'RESOLVED') {
        iconKind = 'FlagResolved';
      } else if (ra.status === 'COMMENTED') {
        iconKind = 'Comment';
        medallionKind = 'outlined';
      }

      return {
        title: activityTitle,
        attributes: [
          {
            label: userLabel,
            detail: ra.user,
          },
          {
            label: l('Comments'),
            detail: (
              <Typography kind='body2' lineLimit={3}>
                {ra.comment || l('No comment')}
              </Typography>
            ),
          },
        ],
        connectorKind: 'past',
        medallionKind,
        icon: iconKind,
        colorTheme: resolvedColorTheme,
        date: dateValue,
        dateFormat,
        isTimeEnabled: false,
        datePosition: 'right',
        size: 'small',
        medallionSize: 'medium',
      };
    });

    return blocks;
  }, [source, l, dateFormat]);

  if (source.length === 0) {
    return null;
  }

  return <Timeline timelineBlocksData={timelineBlocksData} />;
}

AuditLogViewerSidePanelHistory.defaultProps = {
  activities: undefined,
};

export default AuditLogViewerSidePanelHistory;
